import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Eye, GraduationCap, Award,
  BookOpen, DollarSign, Clock, Users, X, ArrowUpDown, ChevronDown,
} from 'lucide-react';
import toast from '../../../../utils/toast';
import { FilterButton } from '../../../../components/UI/FilterButton';
import { InfoBadge } from '../../../../components/UI/InfoBadge';
import { StatusBadge } from '../../../../components/UI/StatusBadge';
import { TradeBadge } from '../../../../components/UI/TradeBadge';
import { Pagination } from '../../../../components/UI/Pagination';
import Portal from '../../../../components/UI/Portal';
import PillToggle from '../../../../components/UI/PillToggle';
import api from '../../../../services/api';
import DebouncedSearchInput from '../../../../components/UI/DebouncedSearchInput';
import { usePersistence } from '../../../../hooks/usePersistence';
import {
  getDepartmentBadgeColor,
  getDepartmentShortName,
} from '../../../../config/departmentConfig';
import { isTradeRedundantWithDepartment } from '../../../../config/tradeConfig';
import { formatDate as formatDateCanonical, parseLocalDate } from '../../../../utils/dateUtils';

const CLASSIFICATION_OPTIONS = [
  { id: 'all', label: 'All' },
  { id: 'workers', label: 'Workers' },
  { id: 'staff', label: 'Staff' },
];

const PAGE_SIZE = 15;

const VALID_STATUS_TRANSITIONS = {
  pending_registration: ['pending_confirmation', 'in_progress', 'cancelled'],
  pending_confirmation: ['in_progress', 'cancelled'],
  in_progress: ['completed', 'cancelled'],
  cancelled: ['pending_registration'],
  completed: [],
};

const getAuthToken = () =>
  localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');

const CATEGORY_OPTIONS = [
  { value: '', label: 'All Categories' },
  { value: 'safety', label: 'Safety & Health' },
  { value: 'compliance', label: 'Compliance & Audit' },
  { value: 'professional_development', label: 'Professional Development' },
  { value: 'technical_skills', label: 'Technical Skills' },
  { value: 'education_degree', label: 'Education / Degree' },
  { value: 'conference_seminar', label: 'Conference / Seminar' },
  { value: 'other', label: 'Other' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'completed', label: 'Completed' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'pending_registration', label: 'Pending Registration' },
  { value: 'pending_confirmation', label: 'Pending Confirmation' },
];

const WorkerIdentityBadges = ({ record }) => {
  const showTrade = (record.trade_code || record.trade_name)
    && !isTradeRedundantWithDepartment(
      record.department_at_training,
      record.trade_code,
      record.trade_name,
    );

  if (!record.department_at_training && !showTrade) return null;

  return (
    <div className="mt-1 flex flex-wrap items-center gap-1.5">
      {record.department_at_training && (
        <InfoBadge
          color={getDepartmentBadgeColor(record.department_at_training)}
          label={getDepartmentShortName(record.department_at_training)}
          title={record.department_at_training}
          size="xs"
        />
      )}
      {showTrade && (
        <TradeBadge
          code={record.trade_code}
          name={record.trade_name}
          size="xs"
        />
      )}
    </div>
  );
};

/** Read-only detail cell — DS field-label + field-display (view mode). */
const DetailField = ({ label, children, className = '', valueClassName = '' }) => (
  <div className={className}>
    <dt className="field-label">{label}</dt>
    <dd className={`field-display mt-0.5 ${valueClassName}`.trim()}>
      {children}
    </dd>
  </div>
);

/** Badge values sit outside field-display — chips-in-pills look like empty inputs. */
const DetailBadgeField = ({ label, children, className = '' }) => (
  <div className={className}>
    <dt className="field-label">{label}</dt>
    <dd className="mt-1 flex flex-wrap items-center gap-1.5 min-h-[32px]">
      {children}
    </dd>
  </div>
);

const TrainingRecordsTab = () => {
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [budgetYearFilter, setBudgetYearFilter] = useState('');
  const [availableYears, setAvailableYears] = useState([]);
  const [classificationFilter, setClassificationFilter] = usePersistence(
    'ld-training-records-classification',
    'all',
    ['all', 'workers', 'staff'],
  );

  // Detail modal
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Sorting
  const [sortField, setSortField] = useState('training_start_date');
  const [sortDir, setSortDir] = useState('desc');

  const activeFilterCount = [statusFilter, categoryFilter, budgetYearFilter].filter(Boolean).length;

  const buildQueryParams = useCallback(() => {
    const params = new URLSearchParams();
    params.set('page', currentPage);
    params.set('page_size', PAGE_SIZE);
    if (searchQuery) params.set('search', searchQuery);
    if (statusFilter) params.set('status', statusFilter);
    if (categoryFilter) params.set('training_category', categoryFilter);
    if (budgetYearFilter) params.set('budget_year', budgetYearFilter);
    if (classificationFilter && classificationFilter !== 'all') {
      params.set('employment_classification', classificationFilter);
    }
    const ordering = sortDir === 'desc' ? `-${sortField}` : sortField;
    params.set('ordering', ordering);
    return params.toString();
  }, [currentPage, searchQuery, statusFilter, categoryFilter, budgetYearFilter, classificationFilter, sortField, sortDir]);

  const fetchAvailableYears = useCallback(async () => {
    try {
      const token = getAuthToken();
      const res = await fetch('/api/v1/training-records/available_years/', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const years = await res.json();
      setAvailableYears(years);
    } catch (err) {
      console.error('Failed to load available years:', err);
    }
  }, []);

  const fetchRecords = useCallback(async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      const res = await fetch(`/api/v1/training-records/?${buildQueryParams()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRecords(data.results || data);
      setTotalCount(data.count ?? (data.results ? data.results.length : data.length));
    } catch (err) {
      console.error('Failed to load training records:', err);
      toast.error('Failed to load training records');
    } finally {
      setLoading(false);
    }
  }, [buildQueryParams]);

  const fetchSummary = useCallback(async () => {
    try {
      const token = getAuthToken();
      const params = new URLSearchParams();
      if (budgetYearFilter) params.set('budget_year', budgetYearFilter);
      if (classificationFilter && classificationFilter !== 'all') {
        params.set('employment_classification', classificationFilter);
      }
      const res = await fetch(`/api/v1/training-records/summary/?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSummary(await res.json());
    } catch (err) {
      console.error('Failed to load summary:', err);
      toast.error('Failed to load training summary');
    }
  }, [budgetYearFilter, classificationFilter]);

  useEffect(() => {
    fetchAvailableYears();
  }, [fetchAvailableYears]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, categoryFilter, budgetYearFilter, classificationFilter]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('');
    setCategoryFilter('');
    setBudgetYearFilter('');
  };

  const formatCurrency = (val) => {
    if (val == null) return '-';
    return `$${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Table cells use '-' for empty; dateUtils returns 'N/A' — map empty only.
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const raw = String(dateStr);
    const value = raw.includes('T')
      ? dateStr
      : (parseLocalDate(raw.split('T')[0]) || dateStr);
    const formatted = formatDateCanonical(value);
    return formatted === 'N/A' || formatted === 'Invalid Date' ? '-' : formatted;
  };

  const formatDateRange = (start, end, raw) => {
    if (!start && !end) return raw || '-';
    if (start === end || !end) return formatDate(start);
    return `${formatDate(start)} – ${formatDate(end)}`;
  };

  const SortHeader = ({ field, children, className = '' }) => (
    <th
      className={`px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer select-none hover:text-gray-900 ${className}`}
      onClick={() => handleSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        <ArrowUpDown className={`w-3 h-3 ${sortField === field ? 'text-red-600' : 'text-gray-400'}`} />
      </span>
    </th>
  );

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // Active filter chips
  const activeChips = [];
  if (statusFilter) {
    const label = STATUS_OPTIONS.find(o => o.value === statusFilter)?.label || statusFilter;
    activeChips.push({ key: 'status', label: `Status: ${label}`, clear: () => setStatusFilter('') });
  }
  if (categoryFilter) {
    const label = CATEGORY_OPTIONS.find(o => o.value === categoryFilter)?.label || categoryFilter;
    activeChips.push({ key: 'category', label: `Category: ${label}`, clear: () => setCategoryFilter('') });
  }
  if (budgetYearFilter) {
    activeChips.push({ key: 'year', label: `Year: ${budgetYearFilter}`, clear: () => setBudgetYearFilter('') });
  }

  return (
    <div>
      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 mb-6">
          <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
            <div className="flex items-center">
              <div className="w-9 h-9 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
              </div>
              <div className="ml-2 sm:ml-4 min-w-0">
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{summary.total_records ?? 0}</p>
                <p className="text-xs sm:text-sm text-gray-600 truncate">Total Records</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
            <div className="flex items-center">
              <div className="w-9 h-9 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
              </div>
              <div className="ml-2 sm:ml-4 min-w-0">
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{formatCurrency(summary.total_spend)}</p>
                <p className="text-xs sm:text-sm text-gray-600 truncate">Total Spend</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
            <div className="flex items-center">
              <div className="w-9 h-9 sm:w-12 sm:h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
              </div>
              <div className="ml-2 sm:ml-4 min-w-0">
                <p className="text-lg sm:text-2xl font-bold text-gray-900">
                  {summary.total_hours != null ? `${Number(summary.total_hours).toLocaleString()}h` : '-'}
                </p>
                <p className="text-xs sm:text-sm text-gray-600 truncate">Total Hours</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
            <div className="flex items-center">
              <div className="w-9 h-9 sm:w-12 sm:h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
              </div>
              <div className="ml-2 sm:ml-4 min-w-0">
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{summary.unique_staff ?? 0}</p>
                <p className="text-xs sm:text-sm text-gray-600 truncate">
                  {classificationFilter === 'workers'
                    ? 'Workers Trained'
                    : classificationFilter === 'staff'
                      ? 'Staff Trained'
                      : 'People Trained'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search + Actions Toolbar — Scope → Filter */}
      <div className="flex items-center justify-between gap-3 mb-4">
        <DebouncedSearchInput
          value={searchQuery}
          onSearch={setSearchQuery}
          placeholder="Search by name, staff ID, course, institution..."
          className="max-w-xl"
        />
        <div className="flex items-center gap-2">
          <PillToggle
            value={classificationFilter}
            onChange={setClassificationFilter}
            options={CLASSIFICATION_OPTIONS}
            variant="text"
            theme="admin"
            ariaLabel="Filter by employment classification"
          />
          <FilterButton
            theme="admin"
            isOpen={showFilters}
            activeCount={activeFilterCount}
            onClick={() => setShowFilters(prev => !prev)}
          />
        </div>
      </div>

      {/* Active filter chips */}
      {activeChips.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {activeChips.map(chip => (
            <span key={chip.key} className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
              {chip.label}
              <button onClick={chip.clear} className="hover:text-red-900" aria-label={`Clear ${chip.label}`}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          <button onClick={clearFilters} className="btn btn-admin-outline btn-sm text-xs px-3 py-1">
            Clear all
          </button>
        </div>
      )}

      {/* Collapsible Filter Panel */}
      {showFilters && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 rounded-xl border">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
            <select
              className="select-admin w-full"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
            <select
              className="select-admin w-full"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              {CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Budget Year</label>
            <select
              className="select-admin w-full"
              value={budgetYearFilter}
              onChange={(e) => setBudgetYearFilter(e.target.value)}
            >
              <option value="">All Years</option>
              {availableYears.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex justify-center items-center py-16">
          <div className="w-8 h-8 border-4 border-red-200 border-t-red-600 rounded-full animate-spin" />
        </div>
      ) : records.length === 0 ? (
        <div className="text-center py-16">
          <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Training Records Found</h3>
          <p className="text-gray-500">
            {searchQuery || statusFilter || categoryFilter || budgetYearFilter
              ? 'Try adjusting your search or filters.'
              : 'No training records have been imported yet.'}
          </p>
        </div>
      ) : (
        <>
          {/* Mobile card view */}
          <div className="md:hidden space-y-3">
            {records.map((rec) => (
              <div key={rec.id} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col h-full">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">{rec.staff_name}</div>
                    <div className="text-xs text-gray-500 truncate">{rec.staff_id_display}</div>
                    <WorkerIdentityBadges record={rec} />
                  </div>
                  <div className="flex-shrink-0">
                    <StatusBadge status={rec.status} />
                  </div>
                </div>
                <div className="mt-3 space-y-2 flex-1">
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-gray-500 flex-shrink-0">Course</span>
                    <span className="text-gray-900 text-right truncate min-w-0" title={rec.course_title}>{rec.course_title}</span>
                  </div>
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-gray-500 flex-shrink-0">Provider</span>
                    <span className="text-gray-700 text-right truncate min-w-0">{rec.provider_name || rec.institution_name || '—'}</span>
                  </div>
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-gray-500 flex-shrink-0">Dates</span>
                    <span className="text-gray-700 text-right min-w-0">{formatDateRange(rec.training_start_date, rec.training_end_date, rec.training_dates_raw)}</span>
                  </div>
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-gray-500 flex-shrink-0">Hours</span>
                    <span className="text-gray-700 text-right min-w-0">{rec.duration_hours ? `${Number(rec.duration_hours)}h` : rec.duration_raw || '—'}</span>
                  </div>
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-gray-500 flex-shrink-0">Fee</span>
                    <span className="text-gray-900 text-right font-medium min-w-0">{formatCurrency(rec.course_fee)}</span>
                  </div>
                </div>
                <div className="card-action-footer justify-end">
                  <button
                    className="icon-button-admin"
                    title="View details"
                    aria-label="View details"
                    onClick={() => setSelectedRecord(rec)}
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="overflow-x-auto hidden md:block">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <SortHeader field="record_staff_id__first_name">Employee</SortHeader>
                  <SortHeader field="course_title">Course</SortHeader>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Provider</th>
                  <SortHeader field="training_start_date">Dates</SortHeader>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Hrs</th>
                  <SortHeader field="course_fee" className="text-right">Fee</SortHeader>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {records.map((rec) => (
                    <tr key={rec.id} className="table-row">
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{rec.staff_name}</div>
                        <div className="text-xs text-gray-500">{rec.staff_id_display}</div>
                        <WorkerIdentityBadges record={rec} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900 max-w-xs truncate" title={rec.course_title}>
                          {rec.course_title}
                        </div>
                        <div className="text-xs text-gray-500">{rec.category_display}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-700 max-w-[160px] truncate" title={rec.provider_name || rec.institution_name}>
                          {rec.provider_name || rec.institution_name || '-'}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                        {formatDateRange(rec.training_start_date, rec.training_end_date, rec.training_dates_raw)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                        {rec.duration_hours ? `${Number(rec.duration_hours)}h` : rec.duration_raw || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right whitespace-nowrap font-medium">
                        {formatCurrency(rec.course_fee)}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={rec.status} />
                      </td>
                      <td className="px-2 py-3">
                        <button
                          className="icon-button-admin"
                          title="View details"
                          aria-label="View details"
                          onClick={() => setSelectedRecord(rec)}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-4">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              totalCount={totalCount}
              pageSize={PAGE_SIZE}
              theme="admin"
              noun="records"
            />
          </div>
        </>
      )}
      {/* Detail Modal */}
      {selectedRecord && (
        <TrainingRecordDetailModal
          record={selectedRecord}
          onClose={() => setSelectedRecord(null)}
          onStatusChange={(updatedRecord) => {
            setRecords(prev => prev.map(r => r.id === updatedRecord.id ? updatedRecord : r));
            setSelectedRecord(updatedRecord);
            fetchSummary();
          }}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
          formatDateRange={formatDateRange}
        />
      )}
    </div>
  );
};

const TrainingRecordDetailModal = ({ record: rec, onClose, onStatusChange, formatCurrency, formatDate, formatDateRange }) => {
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [changingStatus, setChangingStatus] = useState(false);
  const statusMenuRef = useRef(null);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  useEffect(() => {
    if (!showStatusMenu) return;
    const handleClickOutside = (e) => {
      if (statusMenuRef.current && !statusMenuRef.current.contains(e.target)) {
        setShowStatusMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showStatusMenu]);

  const validTransitions = VALID_STATUS_TRANSITIONS[rec.status] || [];
  const edu = rec.linked_education_detail;
  const cert = rec.linked_certificate_detail;
  const showTrade = (rec.trade_code || rec.trade_name)
    && !isTradeRedundantWithDepartment(
      rec.department_at_training,
      rec.trade_code,
      rec.trade_name,
    );

  const handleStatusChange = async (newStatus) => {
    setShowStatusMenu(false);
    setPendingStatus(newStatus);
  };

  const confirmStatusChange = async () => {
    if (!pendingStatus) return;
    setChangingStatus(true);
    try {
      const res = await api.changeTrainingRecordStatus(rec.id, pendingStatus);
      const updated = res.data.record;
      toast.success(res.data.message);
      onStatusChange(updated);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to change status';
      toast.error(msg);
    } finally {
      setChangingStatus(false);
      setPendingStatus(null);
    }
  };

  return (
    <Portal>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-container modal-container-lg" onClick={(e) => e.stopPropagation()}>
          {/* Header — status anchors beside title (DS modal-header status pattern) */}
          <div className="modal-header">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="modal-title truncate">Training Record Details</h3>
                <div className="relative flex-shrink-0" ref={statusMenuRef}>
                  <button
                    type="button"
                    className={`inline-flex items-center gap-1.5 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 ${
                      validTransitions.length > 0
                        ? 'cursor-pointer group'
                        : 'cursor-default'
                    }`}
                    onClick={() => validTransitions.length > 0 && setShowStatusMenu((p) => !p)}
                    disabled={validTransitions.length === 0}
                    aria-haspopup={validTransitions.length > 0 ? 'menu' : undefined}
                    aria-expanded={validTransitions.length > 0 ? showStatusMenu : undefined}
                    title={
                      validTransitions.length > 0
                        ? 'Click to change status'
                        : 'No status changes available'
                    }
                  >
                    <StatusBadge status={rec.status} showIcon />
                    {validTransitions.length > 0 && (
                      <ChevronDown className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600 transition-colors" />
                    )}
                  </button>
                  {showStatusMenu && (
                    <div
                      className="absolute left-0 top-full mt-1 z-50 bg-white rounded-lg shadow-lg border py-1 min-w-[200px]"
                      role="menu"
                    >
                      <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                        Update training status
                      </div>
                      {validTransitions.map((s) => (
                        <button
                          key={s}
                          type="button"
                          role="menuitem"
                          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 focus-visible:outline-none focus-visible:bg-gray-50"
                          onClick={() => handleStatusChange(s)}
                        >
                          <StatusBadge status={s} />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-0.5 truncate">
                {rec.staff_name} · {rec.staff_id_display}
              </p>
            </div>
            <button onClick={onClose} className="modal-close-admin" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="modal-content space-y-5">
            {/* Status Change Confirmation */}
            {pendingStatus && (
              <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-xl border border-amber-200">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">Confirm status change</p>
                  <p className="text-xs text-gray-600 mt-0.5 flex flex-wrap items-center gap-2">
                    <StatusBadge status={rec.status} />
                    <span className="text-gray-400">&rarr;</span>
                    <StatusBadge status={pendingStatus} />
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    className="btn btn-admin-outline btn-sm text-xs"
                    onClick={() => setPendingStatus(null)}
                    disabled={changingStatus}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-admin btn-sm text-xs"
                    onClick={confirmStatusChange}
                    disabled={changingStatus}
                  >
                    {changingStatus ? 'Saving...' : 'Confirm'}
                  </button>
                </div>
              </div>
            )}

            {/* Course + spend — one scan surface for HR (what / who / when / cost) */}
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Course details
              </h4>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <DetailField label="Course title" className="sm:col-span-2">
                  {rec.course_title}
                </DetailField>
                <DetailField label="Provider / institution" className="sm:col-span-2">
                  {rec.provider_name || rec.institution_name || '—'}
                </DetailField>
                {rec.category_display && (
                  <DetailBadgeField label="Category">
                    <InfoBadge color="slate" label={rec.category_display} size="md" />
                  </DetailBadgeField>
                )}
                {rec.department_at_training && (
                  <DetailBadgeField label="Department">
                    <InfoBadge
                      color={getDepartmentBadgeColor(rec.department_at_training)}
                      label={getDepartmentShortName(rec.department_at_training)}
                      title={rec.department_at_training}
                      size="md"
                    />
                  </DetailBadgeField>
                )}
                {showTrade && (
                  <DetailBadgeField label="Trade">
                    <TradeBadge code={rec.trade_code} name={rec.trade_name} size="md" />
                  </DetailBadgeField>
                )}
                <DetailField label="Training dates">
                  {formatDateRange(
                    rec.training_start_date,
                    rec.training_end_date,
                    rec.training_dates_raw,
                  )}
                </DetailField>
                <DetailField label="Duration">
                  {rec.duration_hours
                    ? `${Number(rec.duration_hours)} hours`
                    : rec.duration_raw || '—'}
                </DetailField>
                <DetailField
                  label="Course fee"
                  className={rec.budget_year ? '' : 'sm:col-span-2'}
                  valueClassName="font-semibold text-gray-900"
                >
                  {formatCurrency(rec.course_fee)}
                </DetailField>
                {rec.budget_year ? (
                  <DetailField label="Budget year">
                    FY {rec.budget_year}
                  </DetailField>
                ) : null}
                {rec.course_fee_raw && rec.course_fee_raw !== String(rec.course_fee) && (
                  <DetailField
                    label="Fee (original)"
                    className="sm:col-span-2"
                    valueClassName="text-gray-500"
                  >
                    {rec.course_fee_raw}
                  </DetailField>
                )}
              </dl>
            </div>

            {/* Linked Credentials — only when present (empty state wastes review space) */}
            {(edu || cert) && (
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Linked credentials
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {edu && (
                    <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-xl border border-blue-100">
                      <div className="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <GraduationCap className="w-4.5 h-4.5 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900">{edu.qualification}</p>
                        {edu.field_of_study && (
                          <p className="text-xs text-gray-600">{edu.field_of_study}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-0.5">
                          {edu.institution}
                          {edu.start_year || edu.end_year
                            ? ` · ${edu.start_year || '?'}–${edu.is_ongoing ? 'Present' : edu.end_year || '?'}`
                            : ''}
                        </p>
                        {edu.qualification_level && (
                          <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-700">
                            {edu.qualification_level}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  {cert && (
                    <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-xl border border-amber-100">
                      <div className="w-9 h-9 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Award className="w-4.5 h-4.5 text-amber-600" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{cert.name}</p>
                          {cert.short_name && (
                            <span className="text-xs text-gray-500 flex-shrink-0">({cert.short_name})</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-600">{cert.issuing_organization}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {cert.issue_date ? `Issued ${formatDate(cert.issue_date)}` : ''}
                          {cert.expiry_date ? ` · Expires ${formatDate(cert.expiry_date)}` : ''}
                          {cert.is_lifetime ? ' · Lifetime' : ''}
                        </p>
                        {cert.certificate_number && (
                          <p className="text-xs text-gray-400 mt-0.5">#{cert.certificate_number}</p>
                        )}
                        <div className="mt-1">
                          <StatusBadge
                            status={cert.is_expired ? 'failed' : 'active'}
                            label={cert.is_expired ? 'Expired' : cert.is_lifetime ? 'Lifetime' : 'Active'}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Notes */}
            {rec.notes && (
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Notes</h4>
                <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-lg p-3 border">
                  {rec.notes}
                </p>
              </div>
            )}

            {/* Metadata */}
            <div className="border-t pt-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs text-gray-400">
                <div>
                  <span className="block font-medium text-gray-500">Origin</span>
                  {rec.origin_display}
                </div>
                {rec.migration_csv_sn != null && (
                  <div>
                    <span className="block font-medium text-gray-500">CSV S/N</span>
                    {rec.migration_csv_sn}
                  </div>
                )}
                <div>
                  <span className="block font-medium text-gray-500">Created</span>
                  {formatDate(rec.created_at)}
                </div>
                <div>
                  <span className="block font-medium text-gray-500">Updated</span>
                  {formatDate(rec.updated_at)}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button onClick={onClose} className="btn btn-admin-outline">
              Close
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default TrainingRecordsTab;
